import { Provider } from 'react-redux';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import cn from 'classnames';
import {
  useAppInitialization,
  useSplitIdInOutgoingLinks,
} from '@packages/shared/core/common/hooks';
import ErrorBoundary from '@packages/shared/core/common/components/common/ErrorBoundary';
import ErrorPage from '@packages/shared/core/common/components/lp/Error';
import ServicesProvider, { initServices } from '@packages/shared/core/common/context';
import { FeatureFlagsProvider } from '@packages/shared/core/feature-flags/context';
import AppLoader from '@packages/shared/core/common/components/common/AppLoader';
import IconGradientPreloader from '@packages/shared/svg-sprites/ui/molecules/IconGradientPreloader';
import type { Config } from '@packages/shared/interfaces';
import { createStore, hydrateStoreFromStorage } from '@packages/shared/core/common/store';
import QueryCacheProvider from '@packages/shared/core/queryCache/components/QueryCacheProvider';
import { QueryCacheOptionalFallback } from '@packages/shared/core/queryCache/entities';
import styles from './PageProvider.module.scss';

type PageProviderProps = {
  children: ReactNode;
  config: Config;
  className?: string;
  fallback?: QueryCacheOptionalFallback['fallback'];
};

const PageProvider = ({ children, className = '', config, fallback }: PageProviderProps) => {
  const services = useMemo(() => initServices(config), [config]);
  const store = useMemo(() => createStore(services), [services]);

  useEffect(() => {
    hydrateStoreFromStorage(store);
  }, [store]);

  const { featureFlags } = services;
  const { isLoaderVisible, featureFlagsLoadingStatus } = useAppInitialization(services, store);

  useSplitIdInOutgoingLinks(featureFlags, config.domainRoot);

  return (
    <QueryCacheProvider defaultOptions={config.defaultQueryCacheProviderOptions} fallback={fallback}>
      <ErrorBoundary fallback={<ErrorPage />}>
        <Provider store={store}>
          <ServicesProvider value={services}>
            <FeatureFlagsProvider featureFlags={featureFlags} featureFlagsLoadingStatus={featureFlagsLoadingStatus}>
              <div className={cn(styles.container, className)}>
                <IconGradientPreloader />

                {children}

                {isLoaderVisible && <AppLoader />}
              </div>
            </FeatureFlagsProvider>
          </ServicesProvider>
        </Provider>
      </ErrorBoundary>
    </QueryCacheProvider>
  );
};

export default PageProvider;
